import { useEffect, useState } from "react";
import { canManageMembers } from "../lib/access";
import { formatCep } from "../lib/cep";
import { ApiError } from "../lib/api/client";
import { getMemberActivityHistory, getMemberAttendanceSummary, getMemberProfile } from "../lib/api/endpoints";
import { bookingStatusLabel, sportTypeLabel } from "../lib/enums";
import { money, timeRange } from "../lib/format";
import { useSession } from "../lib/session/SessionContext";
import { C, fieldStyle, font } from "../theme";
import type { MemberActivityHistoryItemDto, MemberAttendanceSummaryDto, MemberAddressDto, MemberDto, MemberProfileDto } from "../types";
import { Button } from "./Button";
import { Card } from "./Card";
import { FieldInput } from "./Field";
import { Label } from "./Label";
import { ProgressBar } from "./ProgressBar";

const HISTORY_PAGE_SIZE = 10;

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.detail ?? error.message : fallback;
}

function addressLine(address: MemberAddressDto): string {
  const street = [address.street, address.number].filter(Boolean).join(", ");
  const city = [address.city, address.state].filter(Boolean).join(" - ");
  const zipCode = address.zipCode ? formatCep(address.zipCode) : null;
  return [street, address.complement, address.neighborhood, city, zipCode].filter(Boolean).join(" · ");
}

function hasSensitiveData(profile: MemberProfileDto): boolean {
  const health = profile.health;
  const measurements = profile.measurements;
  return Boolean(
    health?.bloodType
    || health?.emergencyContactName
    || health?.emergencyContactPhone
    || health?.notes
    || measurements?.weightKg != null
    || measurements?.heightCm != null
    || profile.address
    || profile.notes
    || profile.guardians.length > 0,
  );
}

interface MemberDetailPanelProps {
  member: MemberDto;
  onClose: () => void;
}

export function MemberDetailPanel({ member, onClose }: MemberDetailPanelProps) {
  const { accessToken, activeTenant } = useSession();
  const token = accessToken ?? "";
  const canManage = canManageMembers(activeTenant?.roleSlug ?? null);
  const [tab, setTab] = useState<"history" | "attendance" | "profile">("attendance");
  const [profile, setProfile] = useState<MemberProfileDto | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [summary, setSummary] = useState<MemberAttendanceSummaryDto | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [items, setItems] = useState<MemberActivityHistoryItemDto[] | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [status, setStatus] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setSummary(null);
    setSummaryError(null);
    getMemberAttendanceSummary(token, member.id)
      .then(result => {
        if (!cancelled) setSummary(result);
      })
      .catch(error => {
        if (!cancelled) setSummaryError(errorMessage(error, "Falha ao carregar o resumo de assiduidade."));
      });
    return () => { cancelled = true; };
  }, [token, member.id, reloadKey]);

  useEffect(() => {
    if (!token || !canManage) return;
    let cancelled = false;
    setProfile(null);
    setProfileError(null);
    getMemberProfile(token, member.id)
      .then(result => {
        if (!cancelled) setProfile(result);
      })
      .catch(error => {
        if (!cancelled) setProfileError(errorMessage(error, "Falha ao carregar o perfil do aluno."));
      });
    return () => { cancelled = true; };
  }, [token, member.id, canManage, reloadKey]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setItems(null);
    setHistoryError(null);
    getMemberActivityHistory(token, member.id, {
      page,
      pageSize: HISTORY_PAGE_SIZE,
      from: from || undefined,
      to: to || undefined,
      status: status ? Number(status) : undefined,
    })
      .then(result => {
        if (cancelled) return;
        setItems(result.items);
        setTotalCount(result.totalCount);
      })
      .catch(error => {
        if (cancelled) return;
        setItems([]);
        setHistoryError(errorMessage(error, "Falha ao carregar o histórico de reservas."));
      });
    return () => { cancelled = true; };
  }, [token, member.id, page, from, to, status, reloadKey]);

  const totalPages = Math.max(1, Math.ceil(totalCount / HISTORY_PAGE_SIZE));

  return (
    <div role="dialog" aria-modal="true" aria-label={`Detalhes de ${member.name}`} style={{ position: "fixed", inset: 0, background: C.overlay, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }}>
      <Card style={{ width: "100%", maxWidth: 760, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div>
            <Label style={{ color: C.ink }}>{member.name}</Label>
            <div style={{ color: C.gray, fontSize: 12, marginTop: 2 }}>{member.registrationNumber} · {member.email ?? member.phone ?? "—"}</div>
          </div>
          <Button variant="ghost" onClick={onClose} ariaLabel="Fechar" style={{ fontSize: 16, lineHeight: 1 }}>✕</Button>
        </div>

        <div style={{ display: "flex", gap: 6, margin: "16px 0" }}>
          <button
            onClick={() => setTab("attendance")}
            style={{ fontFamily: font, fontSize: 12, fontWeight: 700, cursor: "pointer", padding: "6px 13px", borderRadius: 20, background: tab === "attendance" ? C.ink : C.white, color: tab === "attendance" ? C.white : C.gray, border: `1px solid ${tab === "attendance" ? C.ink : C.faint}` }}
          >
            Assiduidade
          </button>
          <button
            onClick={() => setTab("history")}
            style={{ fontFamily: font, fontSize: 12, fontWeight: 700, cursor: "pointer", padding: "6px 13px", borderRadius: 20, background: tab === "history" ? C.ink : C.white, color: tab === "history" ? C.white : C.gray, border: `1px solid ${tab === "history" ? C.ink : C.faint}` }}
          >
            Histórico
          </button>
          {canManage && (
            <button
              onClick={() => setTab("profile")}
              style={{ fontFamily: font, fontSize: 12, fontWeight: 700, cursor: "pointer", padding: "6px 13px", borderRadius: 20, background: tab === "profile" ? C.ink : C.white, color: tab === "profile" ? C.white : C.gray, border: `1px solid ${tab === "profile" ? C.ink : C.faint}` }}
            >
              Perfil
            </button>
          )}
        </div>

        {summaryError && tab === "attendance" && (
          <div role="alert" style={{ color: C.red, background: C.bg, borderRadius: 8, padding: 10, fontSize: 12.5 }}>
            {summaryError}
            <Button variant="ghost" onClick={() => setReloadKey(key => key + 1)} style={{ marginLeft: 10 }}>Tentar novamente</Button>
          </div>
        )}

        {tab === "attendance" && (
          <>
            {summary === null && !summaryError && <div style={{ color: C.light, fontSize: 13, padding: "16px 0" }}>Carregando assiduidade...</div>}
            {summary && (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: 10 }}>
                  <Card style={{ padding: 12 }}>
                    <div style={{ color: C.light, fontSize: 11 }}>Total de reservas</div>
                    <div style={{ color: C.ink, fontSize: 20, fontWeight: 700, marginTop: 4 }}>{summary.totalBookings}</div>
                  </Card>
                  <Card style={{ padding: 12 }}>
                    <div style={{ color: C.light, fontSize: 11 }}>Presenças</div>
                    <div style={{ color: C.ink, fontSize: 20, fontWeight: 700, marginTop: 4 }}>{summary.attendedSessions}</div>
                  </Card>
                  <Card style={{ padding: 12 }}>
                    <div style={{ color: C.light, fontSize: 11 }}>No-shows</div>
                    <div style={{ color: C.red, fontSize: 20, fontWeight: 700, marginTop: 4 }}>{summary.noShowCount}</div>
                  </Card>
                  <Card style={{ padding: 12 }}>
                    <div style={{ color: C.light, fontSize: 11 }}>Cancelamentos</div>
                    <div style={{ color: C.ink, fontSize: 20, fontWeight: 700, marginTop: 4 }}>{summary.canceledCount}</div>
                  </Card>
                </div>

                <div style={{ marginTop: 16 }}>
                  <Label>Taxa de presença</Label>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <ProgressBar value={summary.attendanceRate} style={{ flex: 1 }} />
                    <b style={{ color: C.ink, fontSize: 14 }}>{summary.attendanceRate.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%</b>
                  </div>
                </div>

                <div style={{ marginTop: 16 }}>
                  <Label>Esportes favoritos</Label>
                  {summary.favoriteSports.length === 0 && <div style={{ color: C.light, fontSize: 12.5 }}>Sem reservas concluídas ainda.</div>}
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {summary.favoriteSports.map(sport => (
                      <span key={sport.sportType} style={{ background: C.limeSoft, color: C.ink, borderRadius: 12, padding: "4px 10px", fontSize: 11.5, fontWeight: 600 }}>
                        {sportTypeLabel(sport.sportType)} ({sport.count})
                      </span>
                    ))}
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {tab === "profile" && canManage && (
          <>
            {profileError && (
              <div role="alert" style={{ color: C.red, background: C.bg, borderRadius: 8, padding: 10, fontSize: 12.5 }}>
                {profileError}
                <Button variant="ghost" onClick={() => setReloadKey(key => key + 1)} style={{ marginLeft: 10 }}>Tentar novamente</Button>
              </div>
            )}

            {profile === null && !profileError && <div style={{ color: C.light, fontSize: 13, padding: "16px 0" }}>Carregando perfil...</div>}

            {profile && !hasSensitiveData(profile) && (
              <div style={{ color: C.light, fontSize: 13, padding: "16px 0" }}>Nenhum dado sensível cadastrado para este aluno.</div>
            )}

            {profile && hasSensitiveData(profile) && (
              <div style={{ display: "grid", gap: 12 }}>
                {(profile.health && (profile.health.bloodType || profile.health.emergencyContactName || profile.health.emergencyContactPhone || profile.health.notes) || profile.measurements?.weightKg != null || profile.measurements?.heightCm != null) && (
                  <Card style={{ padding: 12 }}>
                    <Label>Saúde e medidas</Label>
                    <div style={{ display: "grid", gap: 4, fontSize: 12.5, color: C.gray }}>
                      {profile.health?.bloodType && <div><b style={{ color: C.ink }}>Tipo sanguíneo:</b> {profile.health.bloodType}</div>}
                      {profile.health?.emergencyContactName && <div><b style={{ color: C.ink }}>Contato de emergência:</b> {profile.health.emergencyContactName}</div>}
                      {profile.health?.emergencyContactPhone && <div><b style={{ color: C.ink }}>Telefone de emergência:</b> {profile.health.emergencyContactPhone}</div>}
                      {profile.measurements?.weightKg != null && <div><b style={{ color: C.ink }}>Peso:</b> {profile.measurements.weightKg.toLocaleString("pt-BR")} kg</div>}
                      {profile.measurements?.heightCm != null && <div><b style={{ color: C.ink }}>Altura:</b> {profile.measurements.heightCm.toLocaleString("pt-BR")} cm</div>}
                      {profile.health?.notes && <div><b style={{ color: C.ink }}>Observações:</b> {profile.health.notes}</div>}
                    </div>
                  </Card>
                )}

                {profile.address && (
                  <Card style={{ padding: 12 }}>
                    <Label>Endereço</Label>
                    <div style={{ fontSize: 12.5, color: C.gray }}>{addressLine(profile.address) || "Endereço não informado."}</div>
                  </Card>
                )}

                {profile.notes && (
                  <Card style={{ padding: 12 }}>
                    <Label>Observações gerais</Label>
                    <div style={{ fontSize: 12.5, color: C.gray, whiteSpace: "pre-wrap" }}>{profile.notes}</div>
                  </Card>
                )}

                {profile.guardians.length > 0 && (
                  <Card style={{ padding: 12 }}>
                    <Label>Responsáveis</Label>
                    <div style={{ display: "grid", gap: 8 }}>
                      {profile.guardians.map(guardian => (
                        <div key={guardian.id} style={{ fontSize: 12.5, color: C.gray }}>
                          <div style={{ color: C.ink, fontWeight: 600 }}>{guardian.name}</div>
                          {guardian.phone && <div>{guardian.phone}</div>}
                          {guardian.address && <div>{addressLine(guardian.address)}</div>}
                          {guardian.notes && <div>{guardian.notes}</div>}
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </div>
            )}
          </>
        )}

        {tab === "history" && (
          <>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 12 }}>
              <label style={{ color: C.gray, fontSize: 12 }}>
                De{" "}
                <input aria-label="De" type="date" value={from} onChange={event => { setFrom(event.target.value); setPage(1); }} style={{ ...fieldStyle, marginLeft: 4 }} />
              </label>
              <label style={{ color: C.gray, fontSize: 12 }}>
                Até{" "}
                <input aria-label="Até" type="date" value={to} onChange={event => { setTo(event.target.value); setPage(1); }} style={{ ...fieldStyle, marginLeft: 4 }} />
              </label>
              <select aria-label="Status" value={status} onChange={event => { setStatus(event.target.value); setPage(1); }} style={fieldStyle}>
                <option value="">Todos os status</option>
                {[1, 2, 3, 4, 5].map(value => (
                  <option key={value} value={value}>{bookingStatusLabel(value)}</option>
                ))}
              </select>
            </div>

            {historyError && (
              <div role="alert" style={{ color: C.red, background: C.bg, borderRadius: 8, padding: 10, fontSize: 12.5 }}>
                {historyError}
                <Button variant="ghost" onClick={() => setReloadKey(key => key + 1)} style={{ marginLeft: 10 }}>Tentar novamente</Button>
              </div>
            )}

            {items === null && !historyError && <div style={{ color: C.light, fontSize: 13, padding: "16px 0" }}>Carregando histórico...</div>}
            {items?.length === 0 && !historyError && <div style={{ color: C.light, fontSize: 13, padding: "16px 0" }}>Nenhuma reserva encontrada para os filtros.</div>}
            {items && items.length > 0 && (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 600, fontSize: 12.5 }}>
                  <thead>
                    <tr style={{ textAlign: "left", color: C.light }}>
                      <th style={{ padding: "8px 10px", borderBottom: `1px solid ${C.faint}` }}>Data</th>
                      <th style={{ padding: "8px 10px", borderBottom: `1px solid ${C.faint}` }}>Quadra</th>
                      <th style={{ padding: "8px 10px", borderBottom: `1px solid ${C.faint}` }}>Modalidade</th>
                      <th style={{ padding: "8px 10px", borderBottom: `1px solid ${C.faint}` }}>Valor</th>
                      <th style={{ padding: "8px 10px", borderBottom: `1px solid ${C.faint}` }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(item => (
                      <tr key={item.bookingId}>
                        <td style={{ padding: "10px", borderBottom: `1px solid ${C.faint}` }}>
                          <div style={{ color: C.ink, fontWeight: 600 }}>{timeRange(item.startTime, item.endTime)}</div>
                          <div style={{ fontSize: 11, color: C.light, marginTop: 2 }}>{new Date(item.startTime).toLocaleDateString("pt-BR")}</div>
                        </td>
                        <td style={{ padding: "10px", borderBottom: `1px solid ${C.faint}`, color: C.gray }}>{item.courtName}</td>
                        <td style={{ padding: "10px", borderBottom: `1px solid ${C.faint}`, color: C.gray }}>{sportTypeLabel(item.sportType)}</td>
                        <td style={{ padding: "10px", borderBottom: `1px solid ${C.faint}`, color: C.gray }}>{money(item.totalPrice)}</td>
                        <td style={{ padding: "10px", borderBottom: `1px solid ${C.faint}` }}>
                          <span style={{ background: item.status === 5 ? C.bg : item.status === 4 ? C.bg : C.limeSoft, color: C.gray, borderRadius: 12, padding: "3px 9px", fontSize: 11 }}>
                            {bookingStatusLabel(item.status)}
                          </span>
                          {item.cancellationReason && <div style={{ fontSize: 11, color: C.red, marginTop: 3 }}>{item.cancellationReason}</div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, marginTop: 14 }}>
              <span style={{ color: C.gray, fontSize: 12 }}>{page} de {totalPages}</span>
              <Button variant="outline" onClick={() => setPage(current => Math.max(1, current - 1))} disabled={page <= 1} ariaLabel="Página anterior">← Anterior</Button>
              <Button variant="outline" onClick={() => setPage(current => Math.min(totalPages, current + 1))} disabled={page >= totalPages} ariaLabel="Próxima página">Próxima →</Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}